import { SELECTED_PORTAL } from '@/shared/config';
import { createClient } from '@/shared/api';
import type { LoginAsClinicMutation, LoginAsClinicMutationVariables } from '@/shared/api';
import LOGIN_AS_CLINIC_MUTATION from '../mutations/loginAsClinic';

/**
 * Exchanges an admin or sales session for a clinic session on behalf of a user and facility. Builds a client aimed at the Admin or Sales portal without an authorized token, runs LOGIN_AS_CLINIC_MUTATION with the clinic portal constant, throws the returned message on failure and yields the authorized token.
 *
 * @param isLoginThroughAdmin - Whether the caller signs in from the Admin portal rather than Sales.
 * @param userId - Identifier of the clinic user to impersonate.
 * @param facilityId - Identifier of the facility to enter.
 * @returns The authorized token for the clinic session.
 */
const handleLoginAsClinic = async ({
  isLoginThroughAdmin,
  userId,
  facilityId,
}: {
  isLoginThroughAdmin?: boolean;
  userId?: string;
  facilityId?: string;
}) => {
  const client = createClient({
    selectedPortal: isLoginThroughAdmin ? 'Admin' : 'Sales',
    isUsingAuthorizedToken: false,
  });
  const loginAsClinicResult = await client.mutate<
    LoginAsClinicMutation,
    LoginAsClinicMutationVariables
  >({
    mutation: LOGIN_AS_CLINIC_MUTATION,
    variables: {
      input: {
        userId,
        facilityId,
        selectedPortal: SELECTED_PORTAL,
      },
    },
  });
  const { data } = loginAsClinicResult;
  // data is guaranteed by the mutation contract; downstream code already assumes it exists
  const { loginAsClinic } = data!;
  if (!loginAsClinic.isSuccess) {
    throw new Error(loginAsClinic.message ?? undefined);
  }
  return loginAsClinic.authorizedToken;
};

export default handleLoginAsClinic;
